import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import { toast } from 'src/@/components/ui/use-toast';
import { Button } from 'src/@/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from 'src/@/components/ui/popover';
import mktAnunciosService from 'src/services/mktAnunciosService';
import mktAccountsService from 'src/services/mktAccountsService';
import MktCompactHeader from 'src/sections/@dashboard/marketing/components/MktCompactHeader';
import MktEmptyState from 'src/sections/@dashboard/marketing/components/MktEmptyState';
import MktLoading from 'src/sections/@dashboard/marketing/components/MktLoading';
import { MKT_ADS_DESIGN as d } from './MktAds.design';
import {
    DATE_PRESET_VALUES,
    DATE_PRESET_QUICK,
    getQuickPresetOptions,
    getDatePresetOptions,
    adResult,
    resultLabel,
    hasDelivery,
    budgetInfo,
    buildStructureIndex,
    formatCurrency,
    formatInt,
    formatPercent,
    formatDecimal
} from './constants';
import MktAdsTable from './MktAdsTable';
import AdsScopeBanner from './components/AdsScopeBanner';
import AdsConnectHero from './components/AdsConnectHero';
import { META_ADS_DISPONIVEL } from 'src/utils/metaAppReview';

const BASE_URL = '/dashboard/marketing/anuncios';

const TAB_IDS = ['campaign', 'adset', 'ad'];

const STATUS_FILTER_IDS = ['all', 'ACTIVE', 'PAUSED', 'with_spend'];

const EMPTY_LEVELS = { campaign: [], adset: [], ad: [] };

// Valores de periodo aceitos na URL (?period=): presets do select + atalhos
// do segmented ('today' so existe como atalho).
const VALID_PRESETS = new Set([...DATE_PRESET_VALUES, ...DATE_PRESET_QUICK]);

function formatSyncTime(date, t) {
    if (!date) return t('ads.syncTime.never');
    const diff = Date.now() - new Date(date).getTime();
    if (diff < 60_000) return t('ads.syncTime.now');
    if (diff < 3_600_000) return t('ads.syncTime.minutesAgo', { count: Math.floor(diff / 60_000) });
    return new Date(date).toLocaleString('pt-BR', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
    });
}

function collectPagesFromAccounts(accounts = []) {
    const map = new Map();
    for (const a of accounts) {
        for (const p of a.account_info?.promote_pages || []) {
            if (!p?.id) continue;
            const key = String(p.id);
            if (!map.has(key)) {
                map.set(key, { id: key, name: p.name || key, picture: p.picture || null });
            }
        }
    }
    return Array.from(map.values());
}

/**
 * KPIs do topo, todos derivados das linhas do nivel atual — nada de comparativo
 * periodo-a-periodo: o backend guarda um unico snapshot de insights por objeto,
 * entao nao existe base anterior pra calcular variacao sem inventar numero.
 */
function buildKpis(rows, currency, level, t) {
    let spend = 0;
    let impressions = 0;
    let reach = 0;
    let clicks = 0;
    let results = 0;
    let dailyActive = 0;
    let delivering = 0;
    const byLabel = new Map();
    let best = null;

    for (const ad of rows) {
        const insights = ad.insights || {};
        spend += Number(insights.spend) || 0;
        impressions += Number(insights.impressions) || 0;
        reach += Number(insights.reach) || 0;
        clicks += Number(insights.clicks) || 0;
        if (hasDelivery(ad)) delivering += 1;

        const result = adResult(ad);
        if (result.value) {
            results += result.value;
            const key = result.labelKey;
            byLabel.set(key, (byLabel.get(key) || 0) + result.value);
            const cpr = (Number(insights.spend) || 0) / result.value;
            if (cpr > 0 && (!best || cpr < best.cpr)) best = { cpr, name: ad.name };
        }

        const budget = budgetInfo(ad, currency, t);
        const active = String(ad.effective_status || ad.status || '').toUpperCase() === 'ACTIVE';
        if (active && budget.daily) dailyActive += budget.daily;
    }

    const cpr = results > 0 ? spend / results : null;
    const ctr = impressions > 0 ? (clicks / impressions) * 100 : null;
    const freq = reach > 0 ? impressions / reach : null;

    const mix = Array.from(byLabel.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 2)
        .map(([key, value]) => `${formatInt(value)} ${resultLabel(key, t)}`)
        .join(' · ');

    const primaryVolume = level === 'ad'
        ? { value: formatInt(impressions), label: t('ads.metrics.impressions') }
        : { value: formatInt(reach), label: t('ads.metrics.reach') };

    return [
        {
            key: 'spend',
            icon: 'eva:credit-card-outline',
            tone: 'brand',
            value: formatCurrency(spend, currency),
            label: t('ads.kpi.investment'),
            sub: dailyActive > 0
                ? t('ads.kpi.dailyActive', { value: formatCurrency(dailyActive, currency) })
                : t('ads.kpi.delivering', { count: delivering })
        },
        {
            key: 'results',
            icon: 'eva:checkmark-circle-2-outline',
            tone: 'success',
            value: formatInt(results),
            label: t('ads.kpi.results'),
            sub: mix || t('ads.kpi.noResults')
        },
        {
            key: 'cpr',
            icon: 'eva:trending-down-outline',
            tone: 'info',
            value: cpr !== null ? formatCurrency(cpr, currency) : '—',
            label: t('ads.kpi.costPerResult'),
            sub: best
                ? t('ads.kpi.bestCpr', { name: best.name, value: formatCurrency(best.cpr, currency) })
                : ''
        },
        {
            key: 'volume',
            icon: 'eva:eye-outline',
            tone: 'warning',
            value: primaryVolume.value,
            label: primaryVolume.label,
            sub: [
                ctr !== null ? t('ads.kpi.ctrValue', { value: formatPercent(ctr) }) : null,
                freq !== null ? t('ads.kpi.frequencyValue', { value: formatDecimal(freq, 1) }) : null
            ].filter(Boolean).join(' · ')
        }
    ];
}

export default function MktAds() {
    const { t, i18n } = useTranslation('marketing');
    const { user } = useAuth();
    const navigate = useNavigate();
    const [levels, setLevels] = useState(EMPTY_LEVELS);
    const [accounts, setAccounts] = useState([]);
    const [capabilities, setCapabilities] = useState(null);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [lastSyncAt, setLastSyncAt] = useState(null);

    // Filtros nascem da URL e sao espelhados de volta nela (effect adiante),
    // pra sobreviverem a ir ao detalhe e voltar, F5 e link compartilhado.
    const [searchParams, setSearchParams] = useSearchParams();

    const [level, setLevel] = useState(() => (TAB_IDS.includes(searchParams.get('level'))
        ? searchParams.get('level')
        : 'campaign'));
    // Escopo da cascata: { campaignId, adsetId? } — so ids; os nomes dos chips
    // sao resolvidos das listas carregadas (a URL nao carrega nome).
    const [scope, setScope] = useState(() => {
        const campaignId = searchParams.get('campaign');
        if (!campaignId) return null;
        const adsetId = searchParams.get('adset');
        return adsetId ? { campaignId, adsetId } : { campaignId };
    });
    const [statusFilter, setStatusFilter] = useState(() => (STATUS_FILTER_IDS.includes(searchParams.get('status'))
        ? searchParams.get('status')
        : 'all'));
    const [accountFilter, setAccountFilter] = useState(() => searchParams.get('account') || '');
    const [pageFilter, setPageFilter] = useState(() => searchParams.get('page') || '');
    const [datePreset, setDatePreset] = useState(() => (VALID_PRESETS.has(searchParams.get('period'))
        ? searchParams.get('period')
        : 'last_30d'));
    const [searchInput, setSearchInput] = useState(() => searchParams.get('q') || '');
    const [search, setSearch] = useState(() => (searchParams.get('q') || '').trim());

    // Espelha o estado na URL. `replace` evita um passo de historico por
    // tecla digitada; so valores fora do padrao entram, pra URL limpa.
    useEffect(() => {
        const params = {};
        if (level !== 'campaign') params.level = level;
        if (statusFilter !== 'all') params.status = statusFilter;
        if (accountFilter) params.account = accountFilter;
        if (pageFilter) params.page = pageFilter;
        if (datePreset !== 'last_30d') params.period = datePreset;
        if (search) params.q = search;
        if (scope?.campaignId) params.campaign = scope.campaignId;
        if (scope?.adsetId) params.adset = scope.adsetId;
        setSearchParams(params, { replace: true });
    }, [level, statusFilter, accountFilter, pageFilter, datePreset, search, scope, setSearchParams]);

    const syncLock = useRef(false);

    const statusOptions = useMemo(() => {
        const labelKeys = {
            all: 'ads.filters.status.all',
            ACTIVE: 'ads.filters.status.active',
            PAUSED: 'ads.filters.status.paused',
            with_spend: 'ads.filters.status.withSpend'
        };
        return STATUS_FILTER_IDS.map((id) => ({ id, label: t(labelKeys[id]) }));
    }, [t]);

    const quickPresets = useMemo(() => getQuickPresetOptions(t), [t]);
    const datePresetOptions = useMemo(() => getDatePresetOptions(t), [t]);

    useEffect(() => {
        const timer = setTimeout(() => setSearch(searchInput.trim()), 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const statusForApi = statusFilter === 'ACTIVE' || statusFilter === 'PAUSED' ? statusFilter : undefined;

    const pages = useMemo(() => {
        const fromCaps = capabilities?.pages || [];
        if (fromCaps.length) return fromCaps;
        return collectPagesFromAccounts(accounts);
    }, [capabilities, accounts]);

    // Os tres niveis vem juntos: a contagem de cada aba e a linha de contexto
    // ("3 conjuntos · 8 anuncios", "em [CAMPANHA X]") so existem cruzando a
    // hierarquia inteira, e assim trocar de aba nao dispara request novo.
    const fetchAll = useCallback(async ({ soft = false } = {}) => {
        if (!soft) setLoading(true);
        try {
            const query = {
                status: statusForApi,
                account_id: accountFilter || undefined,
                page_id: pageFilter || undefined,
                q: search || undefined
            };
            const [campaignRes, adsetRes, adRes, capsRes] = await Promise.all([
                mktAnunciosService.listar(user.clienteId, { ...query, level: 'campaign' }),
                mktAnunciosService.listar(user.clienteId, { ...query, level: 'adset' }),
                mktAnunciosService.listar(user.clienteId, { ...query, level: 'ad' }),
                mktAnunciosService.capacidades(user.clienteId).catch(() => null)
            ]);

            const next = {
                campaign: campaignRes?.data || [],
                adset: adsetRes?.data || [],
                ad: adRes?.data || []
            };
            setLevels(next);
            setCapabilities(capsRes?.data || null);

            const latest = [...next.campaign, ...next.adset, ...next.ad].reduce((acc, r) => {
                const syncedAt = r.synced_at ? new Date(r.synced_at).getTime() : 0;
                return syncedAt > acc ? syncedAt : acc;
            }, 0);
            if (latest) setLastSyncAt(new Date(latest));
        } catch (err) {
            console.error('Erro ao carregar anúncios Meta:', err);
            toast({
                title: t('common.error'),
                description: t('ads.toast.loadFailed'),
                variant: 'destructive'
            });
        } finally {
            if (!soft) setLoading(false);
        }
    }, [user.clienteId, statusForApi, accountFilter, pageFilter, search, t]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    useEffect(() => {
        mktAccountsService.listar(user.clienteId, { provider: 'meta_ads', onlyReal: true })
            .then((res) => {
                const list = res?.data || [];
                setAccounts(list);
                if (!accountFilter && list.length === 1) setAccountFilter(list[0]._id);
                const pageList = collectPagesFromAccounts(list);
                if (!pageFilter && pageList.length === 1) setPageFilter(pageList[0].id);
            })
            .catch(() => {});
    }, [user.clienteId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!pageFilter && pages.length === 1) setPageFilter(pages[0].id);
    }, [pages, pageFilter]);

    // Moeda de exibicao segue o idioma da UI (Real/pt-BR, Dolar/en), nao a
    // moeda real da conta — ver formatCurrency em constants.js.
    const currency = i18n.language === 'en' ? 'USD' : 'BRL';

    // "Com veiculação" nao existe como filtro na Graph API: filtra no cliente,
    // depois de os tres niveis chegarem.
    const visible = useMemo(() => {
        if (statusFilter !== 'with_spend') return levels;
        const keep = (list) => list.filter(hasDelivery);
        return { campaign: keep(levels.campaign), adset: keep(levels.adset), ad: keep(levels.ad) };
    }, [levels, statusFilter]);

    // Com busca ou filtro de status as tres listas sao recortes, entao contar
    // filhos nelas nao descreve a estrutura real (ver buildStructureIndex).
    const structureIndex = useMemo(
        () => buildStructureIndex({
            campaigns: levels.campaign,
            adsets: levels.adset,
            ads: levels.ad,
            trusted: !search && !statusForApi
        }),
        [levels, search, statusForApi]
    );

    // Navegacao em cascata no padrao do Ads Manager: drill numa campanha (ou
    // conjunto) restringe as abas de filhos aos dela, com chip removivel.
    // Campanhas nunca sao filtradas pelo escopo — o escopo so desce.
    const scoped = useMemo(() => {
        if (!scope) return visible;
        return {
            campaign: visible.campaign,
            adset: visible.adset.filter(
                (a) => String(a.parent_ids?.campaign_id) === String(scope.campaignId)
            ),
            ad: visible.ad.filter((a) => (scope.adsetId
                ? String(a.parent_ids?.adset_id) === String(scope.adsetId)
                : String(a.parent_ids?.campaign_id) === String(scope.campaignId)))
        };
    }, [visible, scope]);

    const rows = scoped[level] || [];
    const totalItems = visible.campaign.length + visible.adset.length + visible.ad.length;

    const tabs = useMemo(
        () => TAB_IDS.map((id) => ({ id, label: t(`ads.level.${id}`), count: scoped[id].length })),
        [t, scoped]
    );

    const kpis = useMemo(() => buildKpis(rows, currency, level, t), [rows, currency, level, t]);

    const activeFilterCount = [
        statusFilter !== 'all',
        Boolean(accountFilter),
        Boolean(pageFilter),
        Boolean(search)
    ].filter(Boolean).length;

    const openDetail = (ad) => navigate(`${BASE_URL}/${ad._id}`);

    const handleDrill = (ad, target) => {
        if (ad.level === 'campaign') {
            setScope({ campaignId: String(ad.external_id) });
        } else if (ad.level === 'adset') {
            setScope({
                campaignId: String(ad.parent_ids?.campaign_id || ''),
                adsetId: String(ad.external_id)
            });
        }
        setLevel(target);
    };

    // Nomes dos chips resolvidos das listas (a URL so guarda os ids do escopo).
    const scopeNames = useMemo(() => {
        if (!scope) return null;
        const campaign = levels.campaign.find(
            (c) => String(c.external_id) === String(scope.campaignId)
        );
        const adset = scope.adsetId
            ? levels.adset.find((s) => String(s.external_id) === String(scope.adsetId))
            : null;
        return {
            campaign: campaign?.name || scope.campaignId,
            adset: adset?.name || scope.adsetId
        };
    }, [scope, levels]);

    const handleReconnect = () => {
        window.location.href = '/dashboard/configuracoes/contas-sociais?from=anuncios';
    };

    const handleSync = async () => {
        if (syncLock.current || syncing) return;
        syncLock.current = true;
        setSyncing(true);
        try {
            const res = await mktAnunciosService.sincronizar(user.clienteId, { date_preset: datePreset });
            const { imported = 0, updated = 0, errors = [], message } = res?.data || {};
            const total = imported + updated;

            if (errors.some((e) => e.kind === 'MISSING_SCOPE' || e.kind === 'NO_ACCOUNT')) {
                toast({
                    title: t('ads.toast.syncBlocked'),
                    description: errors[0]?.message || t('ads.toast.syncBlockedDesc'),
                    variant: 'destructive'
                });
            } else if (errors.length && total === 0) {
                toast({
                    title: t('ads.toast.syncFailedTitle'),
                    description: errors[0]?.message || t('ads.toast.metaApiError'),
                    variant: 'destructive'
                });
            } else if (total === 0) {
                toast({
                    title: t('ads.toast.nothingToSync'),
                    description: message || t('ads.toast.noAdsInAccount')
                });
            } else {
                toast({
                    title: t('ads.toast.syncComplete'),
                    description: t('ads.toast.syncResult', { imported, updated })
                });
            }
            setLastSyncAt(new Date());
            await fetchAll({ soft: true });
        } catch (err) {
            const msg = typeof err === 'string' ? err : (err?.message || t('ads.toast.syncFailedGeneric'));
            toast({
                title: t('ads.toast.syncErrorTitle'),
                description: msg,
                variant: 'destructive'
            });
        } finally {
            setSyncing(false);
            syncLock.current = false;
        }
    };

    const needsReconnect = capabilities?.needsReconnect;
    // Reconectar so faz sentido enquanto a Meta tiver como conceder o escopo.
    const podeReconectar = Boolean(needsReconnect) && META_ADS_DISPONIVEL;
    // Nenhuma conta de anúncios legível: o painel não tem o que mostrar, então
    // o corpo inteiro vira o herói de conexão. capabilities null = a chamada de
    // capacidades falhou; nesse caso não afirmamos que falta conexão e a tela
    // segue como antes.
    const semContaDeAnuncios = Boolean(capabilities) && !capabilities.canReadAny;
    const presetLabel = quickPresets.find((p) => p.value === datePreset)?.full
        || datePresetOptions.find((p) => p.value === datePreset)?.label
        || '';

    // Sem conta de anúncios legível a tela inteira é o convite: filtros,
    // período, KPIs e abas só teriam zeros para mostrar, e a única saída é
    // conectar. Fica depois do loading (capabilities null enquanto carrega),
    // então não há piscada de herói antes dos dados chegarem.
    if (semContaDeAnuncios) {
        return (
            <div className={d.page.containerCompact}>
                <MktCompactHeader
                    icon="logos:facebook"
                    iconVariant="brand"
                    title={t('ads.title')}
                    right={META_ADS_DISPONIVEL ? (
                        <div className="h-[52px] flex items-end">
                            <button type="button" className={d.connectHero.headerCta} onClick={handleReconnect}>
                                <Iconify icon="eva:link-2-outline" width={16} />
                                {t('ads.connectMeta')}
                            </button>
                        </div>
                    ) : null}
                />
                <div className={d.page.content}>
                    <AdsConnectHero capabilities={capabilities} onConnect={handleReconnect} />
                </div>
            </div>
        );
    }

    return (
        <div className={d.page.containerCompact}>
            <MktCompactHeader
                icon="logos:facebook"
                iconVariant="brand"
                title={t('ads.title')}
                middle={(
                    <div className="flex-1 h-[52px] flex justify-center items-end gap-3 flex-wrap">
                        <div>
                            <input
                                type="text"
                                className={`${d.compactHeader.filterControl} border border-border bg-card px-3 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 focus:outline-none w-[180px] md:w-[220px]`}
                                placeholder={t('ads.searchPlaceholder')}
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                        </div>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className="h-9 px-3 border-dashed border-border text-xs text-muted-foreground hover:border-violet-500 hover:text-violet-600 hover:bg-violet-500/5 bg-transparent"
                                >
                                    <Iconify icon="mdi:filter-variant" width={16} className="mr-1.5" />
                                    {t('common.filters')}
                                    {activeFilterCount > 0 && (
                                        <span className="ml-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-violet-100 dark:bg-violet-500/20 text-[10px] font-medium text-violet-800 dark:text-violet-300">
                                            {activeFilterCount}
                                        </span>
                                    )}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-80 p-4" align="start">
                                <div className="space-y-4">
                                    <h4 className="font-medium text-sm text-foreground border-b border-border pb-2">{t('common.filters')}</h4>
                                    <div className="space-y-3">
                                        <div>
                                            <label className={d.filters.label}>{t('common.status')}</label>
                                            <select
                                                className={`${d.filters.control} border border-border bg-card px-2 w-full focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 focus:outline-none`}
                                                value={statusFilter}
                                                onChange={(e) => setStatusFilter(e.target.value)}
                                            >
                                                {statusOptions.map((o) => (
                                                    <option key={o.id} value={o.id}>{o.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className={d.filters.label}>{t('ads.filters.page')}</label>
                                            <select
                                                className={`${d.filters.control} border border-border bg-card px-2 w-full focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 focus:outline-none`}
                                                value={pageFilter}
                                                onChange={(e) => setPageFilter(e.target.value)}
                                            >
                                                <option value="">{t('common.allFeminine')}</option>
                                                {pages.map((p) => (
                                                    <option key={p.id} value={p.id}>{p.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className={d.filters.label}>{t('ads.filters.adAccount')}</label>
                                            <select
                                                className={`${d.filters.control} border border-border bg-card px-2 w-full focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 focus:outline-none`}
                                                value={accountFilter}
                                                onChange={(e) => setAccountFilter(e.target.value)}
                                            >
                                                <option value="">{t('common.allFeminine')}</option>
                                                {accounts.map((a) => (
                                                    <option key={a._id} value={a._id}>
                                                        {a.account_name} ({a.account_info?.act_id || a.account_id})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className={d.filters.label}>{t('ads.filters.nextSyncPeriod')}</label>
                                            <select
                                                className={`${d.filters.control} border border-border bg-card px-2 w-full focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 focus:outline-none`}
                                                value={datePreset}
                                                onChange={(e) => setDatePreset(e.target.value)}
                                            >
                                                {datePresetOptions.map((o) => (
                                                    <option key={o.value} value={o.value}>{o.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </PopoverContent>
                        </Popover>
                    </div>
                )}
                right={(
                    <div className="h-[52px] flex items-end">
                        <div className="flex items-center gap-2 md:gap-3">
                            <div className={`${d.segmented.container} hidden lg:flex`} title={t('ads.periodTooltip')}>
                                {quickPresets.map((preset) => (
                                    <button
                                        key={preset.value}
                                        type="button"
                                        className={datePreset === preset.value ? d.segmented.itemActive : d.segmented.item}
                                        onClick={() => setDatePreset(preset.value)}
                                    >
                                        {preset.label}
                                    </button>
                                ))}
                            </div>
                            <span className="hidden xl:inline text-[11px] text-muted-foreground tabular-nums">
                                {formatSyncTime(lastSyncAt, t)}
                            </span>
                            <button
                                type="button"
                                className="flex items-center gap-2 h-9 px-3.5 bg-card border border-border hover:bg-accent text-foreground text-xs font-medium rounded-lg transition-all disabled:opacity-50"
                                onClick={handleSync}
                                disabled={syncing}
                            >
                                <Iconify icon={syncing ? 'eos-icons:loading' : 'eva:refresh-outline'} width={16} />
                                {syncing ? t('ads.syncing') : t('ads.sync')}
                            </button>
                            {podeReconectar && (
                                <button type="button" className={d.connectHero.headerCta} onClick={handleReconnect}>
                                    <Iconify icon="eva:link-2-outline" width={16} />
                                    {t('ads.connectMeta')}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            />

            <div className={d.page.content}>
                <AdsScopeBanner capabilities={capabilities} onReconnect={handleReconnect} />

                {syncing && (
                    <div className="flex items-center gap-2 bg-violet-50 dark:bg-violet-500/10 border border-violet-200 dark:border-violet-500/25 rounded-xl px-4 py-2.5 mb-6 text-sm text-violet-700 dark:text-violet-300">
                        <Iconify icon="eos-icons:loading" width={16} />
                        {t('ads.syncingBanner')}
                    </div>
                )}

                <div className={d.kpi.grid}>
                    {kpis.map((kpi) => (
                        <div key={kpi.key} className={d.kpi.card}>
                            <div className={`${d.kpi.iconBox} ${d.toneSoft[kpi.tone].box}`}>
                                <Iconify icon={kpi.icon} width={18} className={d.toneSoft[kpi.tone].icon} />
                            </div>
                            <div className={d.kpi.body}>
                                <p className={d.kpi.value}>{kpi.value}</p>
                                <p className={d.kpi.label}>{kpi.label}</p>
                                {kpi.sub && <p className={d.kpi.sub}>{kpi.sub}</p>}
                            </div>
                        </div>
                    ))}
                </div>

                <div className={d.tabsRow.wrapper}>
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className={d.tabsRow.container}>
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    type="button"
                                    className={level === tab.id ? d.tabsRow.tabActive : d.tabsRow.tab}
                                    onClick={() => setLevel(tab.id)}
                                >
                                    {tab.label}
                                    <span className={level === tab.id ? d.tabsRow.countActive : d.tabsRow.count}>
                                        {tab.count}
                                    </span>
                                </button>
                            ))}
                        </div>
                        {scope && (
                            <span className={d.tabsRow.scopeChip}>
                                <span className={d.tabsRow.scopeChipLabel}>{t('ads.table.campaign')}:</span>
                                <span className={d.tabsRow.scopeChipName}>{scopeNames?.campaign}</span>
                                <button
                                    type="button"
                                    className={d.tabsRow.scopeChipX}
                                    aria-label={t('ads.cascade.clear')}
                                    onClick={() => setScope(null)}
                                >
                                    <Iconify icon="eva:close-outline" width={13} />
                                </button>
                            </span>
                        )}
                        {scope?.adsetId && (
                            <span className={d.tabsRow.scopeChip}>
                                <span className={d.tabsRow.scopeChipLabel}>{t('ads.table.adset')}:</span>
                                <span className={d.tabsRow.scopeChipName}>{scopeNames?.adset}</span>
                                <button
                                    type="button"
                                    className={d.tabsRow.scopeChipX}
                                    aria-label={t('ads.cascade.clear')}
                                    onClick={() => setScope((s) => ({ campaignId: s.campaignId }))}
                                >
                                    <Iconify icon="eva:close-outline" width={13} />
                                </button>
                            </span>
                        )}
                    </div>
                    <span className={d.tabsRow.note}>
                        {t('ads.periodNote', { preset: presetLabel, when: formatSyncTime(lastSyncAt, t) })}
                    </span>
                </div>

                {loading ? (
                    <MktLoading />
                ) : rows.length === 0 ? (
                    <div className={`${d.surface.card} ${d.surface.cardPad}`}>
                        <MktEmptyState
                            icon={needsReconnect ? 'eva:alert-triangle-outline' : 'mdi:facebook'}
                            title={needsReconnect
                                ? t('ads.empty.permissionsPending')
                                : totalItems > 0
                                    ? t('ads.empty.noneAtLevel', { level: t(`ads.levelSingular.${level}`) })
                                    : t('ads.empty.noneFound')}
                            subtitle={needsReconnect
                                ? t('ads.empty.permissionsDesc')
                                : t('ads.empty.noneDesc')}
                            actionLabel={podeReconectar
                                ? t('ads.empty.updatePermissions')
                                : (syncing ? t('ads.syncing') : t('ads.empty.syncNow'))}
                            onAction={podeReconectar ? handleReconnect : handleSync}
                        />
                    </div>
                ) : (
                    <MktAdsTable
                        ads={rows}
                        currency={currency}
                        level={level}
                        structureIndex={structureIndex}
                        childLists={{ adsets: visible.adset, ads: visible.ad }}
                        onOpen={openDetail}
                        onDrill={handleDrill}
                    />
                )}
            </div>
        </div>
    );
}
